// ─────────────────────────────────────────────────────────────────────────────
// Set a robot's INITIAL POSITION by marking it on its assigned map.
//
// The on-site operator is the person who can actually see where the robot is
// standing, so this is the one robot-configuration field the user portal may
// write. Everything else stays admin-only — the backend enforces that: this
// PATCHes /api/portal/robots/:id/initial-position, which is scoped to the
// robots already granted to the signed-in portal user.
//
// Click the map to drop the pose, drag the arrow (or type degrees) to aim it.
// Saving persists the pose and best-effort pushes SET_INITIAL_POSE to the robot.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { load as yamlLoad } from "js-yaml";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CONFIG from "../../config/config";
import {
  MAP_LOAD_ERROR_MESSAGE,
  ROBOT_POSITION_SAVE_ERROR_MESSAGE,
} from "../../utils/errorMessages";
import { tokens } from "../../theme";
import {
  pxToWorld,
  worldToPx,
  degToRad,
  radToDisplayDeg,
  angleFromPointer,
  drawPoseMarker,
} from "../../utils/mapCoords";

const MAPS_BASE = `${CONFIG.BASE_URL}/api/maps`;
const ROTATE_HANDLE_RADIUS = 22; // px — grab radius around the pin for yaw drag

// P5 binary PGM parser (mirrors LiveRobotPositionViewer's).
const parsePGM = async (pgmBlob) => {
  const data = new Uint8Array(await pgmBlob.arrayBuffer());
  const headerString = new TextDecoder("ascii").decode(data.subarray(0, 2048));
  const lines = headerString.split(/\r?\n/).filter((l) => l.length > 0 && !l.startsWith("#"));
  if (lines[0].trim() !== "P5") throw new Error("Not a valid P5 PGM file");

  const [width, height] = lines[1].trim().split(/\s+/).map(Number);
  const maxGray = Number.parseInt(lines[2].trim(), 10);
  if (!Number.isFinite(width) || !Number.isFinite(height) || !Number.isFinite(maxGray)) {
    throw new TypeError("Invalid PGM header");
  }

  const headerLength = headerString.indexOf(lines[2]) + lines[2].length + 1;
  const pixelsRaw = data.subarray(headerLength, headerLength + width * height);
  return { width, height, pixels: Array.from(pixelsRaw, (v) => (v / maxGray) * 255) };
};

const readMapId = (assignedMap) =>
  (typeof assignedMap === "object" ? assignedMap?._id : assignedMap) || null;

const loadBase64Image = (b64) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode map image"));
    img.src = `data:image/png;base64,${b64}`;
  });

const InitialPositionModal = ({ robot, open, onClose, onSaved }) => {
  const canvasRef = useRef(null);
  const geminiImgRef = useRef(null);
  const isImageModeRef = useRef(false);
  const rotatingRef = useRef(false);
  const poseRef = useRef(null);

  const [pgmData, setPgmData] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [pose, setPose] = useState(null);

  useEffect(() => {
    poseRef.current = pose;
  }, [pose]);

  const mapId = readMapId(robot?.assignedMap);
  const mapName = typeof robot?.assignedMap === "object" ? robot.assignedMap?.name : null;

  // ── Load the map ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !mapId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem("portalToken")}` };
        const [mapRes, yamlRes, pgmRes] = await Promise.all([
          axios.get(`${MAPS_BASE}/${mapId}`, { headers }),
          axios.get(`${MAPS_BASE}/base64-to-yaml/${mapId}`, { responseType: "text", headers }),
          axios.get(`${MAPS_BASE}/convertbase64ToPgm/${mapId}`, { responseType: "blob", headers }),
        ]);
        if (cancelled) return;

        const cfg = yamlLoad(yamlRes.data);
        const parsed = await parsePGM(pgmRes.data);
        const mapDoc = mapRes.data;

        if (mapDoc.switchPgmData && mapDoc.geminiPGMData) {
          geminiImgRef.current = await loadBase64Image(mapDoc.geminiPGMData);
          isImageModeRef.current = true;
          if (cancelled) return;
          setPgmData({ width: parsed.width, height: parsed.height, pixels: null });
        } else {
          isImageModeRef.current = false;
          geminiImgRef.current = null;
          setPgmData(parsed);
        }
        setMapConfig(cfg);

        // Seed from the saved pose only when it belongs to THIS map — a pose from
        // another map is in the wrong frame.
        const saved = robot?.initialPosition;
        if (saved && String(saved.mapId) === String(mapId)) {
          const { x: spx, y: spy } = worldToPx(saved.x, saved.y, cfg, parsed.height);
          setPose({ px: spx, py: spy, x: saved.x, y: saved.y, yawDeg: radToDisplayDeg(saved.yaw) });
        }
      } catch {
        if (!cancelled) setLoadError(MAP_LOAD_ERROR_MESSAGE);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mapId, robot?.initialPosition]);

  // ── Draw ───────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !pgmData) return;

    const { width: w, height: h, pixels } = pgmData;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");

    if (isImageModeRef.current && geminiImgRef.current) {
      ctx.drawImage(geminiImgRef.current, 0, 0, w, h);
    } else if (pixels) {
      const imageData = ctx.createImageData(w, h);
      for (let i = 0; i < pixels.length; i++) {
        const v = pixels[i];
        imageData.data[i * 4] = v;
        imageData.data[i * 4 + 1] = v;
        imageData.data[i * 4 + 2] = v;
        imageData.data[i * 4 + 3] = 255;
      }
      ctx.putImageData(imageData, 0, 0);
    }

    if (pose) {
      drawPoseMarker(ctx, pose.px, pose.py, degToRad(pose.yawDeg), {
        color: "#f59e0b",
        label: "Start",
        // The pose editor keeps its arrow: dragging it is how yaw is set.
        showHeading: true,
      });
    }
  }, [pgmData, pose]);

  useEffect(() => {
    draw();
  }, [draw]);

  // ── Interaction ────────────────────────────────────────────────────────────
  // The canvas is CSS-scaled to fit the dialog, so rect pixels must be scaled
  // back to intrinsic canvas pixels before converting to world coordinates.
  const eventToCanvasPx = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      px: ((e.clientX - rect.left) / rect.width) * canvas.width,
      py: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const handleCanvasClick = (e) => {
    if (!pgmData || !mapConfig || rotatingRef.current) return;
    const { px, py } = eventToCanvasPx(e);

    const current = poseRef.current;
    if (current && Math.hypot(px - current.px, py - current.py) < ROTATE_HANDLE_RADIUS) return;

    const world = pxToWorld(px, py, mapConfig, pgmData.height);
    setPose((prev) => ({ px, py, x: world.x, y: world.y, yawDeg: prev?.yawDeg ?? 0 }));
    setSaveError(null);
  };

  const handleMouseDown = (e) => {
    const current = poseRef.current;
    if (!current) return;
    const { px, py } = eventToCanvasPx(e);
    if (Math.hypot(px - current.px, py - current.py) < ROTATE_HANDLE_RADIUS) {
      rotatingRef.current = true;
      setRotating(true);
    }
  };

  useEffect(() => {
    const onMove = (e) => {
      const current = poseRef.current;
      const canvas = canvasRef.current;
      if (!rotatingRef.current || !current || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const px = ((e.clientX - rect.left) / rect.width) * canvas.width;
      const py = ((e.clientY - rect.top) / rect.height) * canvas.height;
      setPose((p) => (p ? { ...p, yawDeg: angleFromPointer(current.px, current.py, px, py) } : p));
    };
    const onUp = () => {
      if (!rotatingRef.current) return;
      rotatingRef.current = false;
      setRotating(false);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // ── Persist ────────────────────────────────────────────────────────────────
  const persist = async (initialPosition) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await axios.patch(
        `${CONFIG.BASE_URL}/api/portal/robots/${robot._id}/initial-position`,
        { initialPosition },
        { headers: { Authorization: `Bearer ${localStorage.getItem("portalToken")}` } }
      );
      onSaved?.(data);
      onClose?.();
    } catch {
      setSaveError(ROBOT_POSITION_SAVE_ERROR_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!pose) return;
    persist({ x: pose.x, y: pose.y, yaw: degToRad(pose.yawDeg), mapId });
  };

  const noMap = !mapId;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            background: tokens.surface,
            border: `1px solid ${tokens.border}`,
          },
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{
            p: "16px 20px",
            background: tokens.surface2,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              background: tokens.accent,
              flexShrink: 0,
            }}
          >
            <MyLocationIcon sx={{ fontSize: 19, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: tokens.text }}>
              Set Initial Position
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.text3, mt: "2px" }}>
              {mapName ? `${robot?.name || "Robot"} · ${mapName}` : robot?.name || "Robot"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving} sx={{ color: tokens.text3 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5 }}>
        {noMap && (
          <Box sx={noticeSx(tokens.warning, tokens.warningSoft)}>
            This robot has no assigned map. An initial position only has meaning inside a map&apos;s
            coordinate frame — ask an administrator to assign one.
          </Box>
        )}

        {!noMap && loadError && (
          <Box sx={noticeSx(tokens.danger, tokens.dangerSoft)}>
            Could not load the map: {loadError}
          </Box>
        )}

        {!noMap && !loadError && (
          <>
            <Typography sx={{ fontSize: 12.5, color: tokens.text2, mb: 1.25 }}>
              Click anywhere on the map to place the robot&apos;s starting point, then drag the
              arrow to set which way it faces.
            </Typography>

            <Box
              sx={{
                position: "relative",
                background: tokens.surface2,
                border: `1px solid ${tokens.border}`,
                borderRadius: "12px",
                p: 1,
                display: "grid",
                placeItems: "center",
                minHeight: 260,
                overflow: "auto",
              }}
            >
              {loading ? (
                <Stack alignItems="center" gap={1.5} sx={{ py: 5 }}>
                  <CircularProgress size={26} sx={{ color: tokens.accent }} />
                  <Typography sx={{ fontSize: 13, color: tokens.text3 }}>Loading map…</Typography>
                </Stack>
              ) : (
                <canvas
                  ref={canvasRef}
                  onClick={handleCanvasClick}
                  onMouseDown={handleMouseDown}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "58vh",
                    borderRadius: 8,
                    display: "block",
                    cursor: rotating ? "grabbing" : "crosshair",
                    imageRendering: "pixelated",
                  }}
                />
              )}
            </Box>

            <Stack direction="row" gap={1.25} flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
              <Readout label="X" value={pose ? `${pose.x.toFixed(3)} m` : "—"} />
              <Readout label="Y" value={pose ? `${pose.y.toFixed(3)} m` : "—"} />
              <Stack direction="row" alignItems="center" gap={0.75}>
                <Typography sx={readoutLabelSx}>YAW</Typography>
                <TextField
                  type="number"
                  size="small"
                  value={pose ? pose.yawDeg : ""}
                  disabled={!pose}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    if (!Number.isFinite(raw)) return;
                    const yawDeg = ((Math.round(raw) % 360) + 360) % 360;
                    setPose((p) => (p ? { ...p, yawDeg } : p));
                  }}
                  inputProps={{
                    min: 0,
                    max: 359,
                    style: {
                      fontFamily: "'JetBrains Mono',monospace",
                      fontSize: 12.5,
                      padding: "6px 8px",
                    },
                  }}
                  sx={{ width: 88 }}
                />
                <Typography sx={{ fontSize: 12, color: tokens.text3 }}>°</Typography>
              </Stack>
            </Stack>

            {saveError && (
              <Box sx={{ ...noticeSx(tokens.danger, tokens.dangerSoft), mt: 1.5 }}>{saveError}</Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: "14px 20px",
          background: tokens.surface2,
          borderTop: `1px solid ${tokens.border}`,
          gap: 1,
        }}
      >
        {robot?.initialPosition && (
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            disabled={saving}
            onClick={() => persist(null)}
            sx={{
              mr: "auto",
              color: tokens.danger,
              background: tokens.dangerSoft,
              "&:hover": { background: tokens.dangerSoft, filter: "brightness(0.95)" },
            }}
          >
            Clear
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{ borderColor: tokens.border, color: tokens.text2 }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving || !pose || noMap}
        >
          {saving ? "Saving…" : "Save Position"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Local style helpers ──────────────────────────────────────────────────────
const noticeSx = (color, bg) => ({
  background: bg,
  color,
  borderRadius: "10px",
  p: "10px 12px",
  fontSize: 12.5,
  lineHeight: 1.5,
  border: `1px solid ${color}30`,
});

const readoutLabelSx = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: ".06em",
  color: tokens.text3,
};

const Readout = ({ label, value }) => (
  <Stack direction="row" alignItems="center" gap={0.75}>
    <Typography sx={readoutLabelSx}>{label}</Typography>
    <Typography
      sx={{
        fontFamily: "'JetBrains Mono',monospace",
        fontSize: 12.5,
        color: tokens.text,
        background: tokens.surface2,
        border: `1px solid ${tokens.border}`,
        borderRadius: "7px",
        px: 1.1,
        py: 0.6,
        minWidth: 82,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

Readout.propTypes = { label: PropTypes.string, value: PropTypes.node };

InitialPositionModal.propTypes = {
  robot: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSaved: PropTypes.func,
};

export default InitialPositionModal;
